import {useAuthService} from '~/composables/useAuthService';
import {useRuntimeConfig} from '#app';

/**
 * Authenticated file downloads (PDF receipts, Excel statements). The API
 * streams the file body directly (no S3 URL), so a plain <a href> can't carry
 * the bearer token — fetch it as a blob and click a temporary object URL.
 *
 *   const {downloadFile} = useFileDownload();
 *   await downloadFile(endpoints.payments.chargeReceipt(charge.id));
 */
export function useFileDownload() {
  const config = useRuntimeConfig().public.grantThomasNuxt;
  const {accessToken, impersonatingAccessToken} = useAuthService();

  // Fetch the file as a blob with the bearer token. The file name comes from
  // Content-Disposition when the browser can read it, else null; the
  // response headers ride along for callers that read their own. Throws the
  // Response when the server refuses, so the caller can read its body.
  const fetchFile = async (endpoint, query = null) => {
    const headers = {};
    if (endpoint.requiresAuth) {
      const token = (impersonatingAccessToken.value != null && impersonatingAccessToken.value.length > 10)
          ? impersonatingAccessToken.value
          : accessToken.value;
      headers.Authorization = 'Bearer ' + token;
    }

    let url = `${config.apiBaseUrl}${endpoint.path}`;
    if (query && Object.keys(query).length > 0) {
      url += (url.includes('?') ? '&' : '?') + new URLSearchParams(query).toString();
    }

    const response = await fetch(url, {method: 'GET', headers});
    if (!response.ok) {
      throw response;
    }

    // Filename from Content-Disposition, e.g. attachment; filename=Tap...pdf
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const fileName = match ? decodeURIComponent(match[1]) : null;

    return {blob: await response.blob(), fileName, headers: response.headers};
  };

  // Save a blob the browser already holds, under the given name.
  const saveFile = (blob, fileName) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const downloadFile = async (endpoint, query = null) => {
    const {blob, fileName} = await fetchFile(endpoint, query);
    saveFile(blob, fileName ?? 'download');
  };

  return {downloadFile, fetchFile, saveFile};
}
